import { Info } from '@phosphor-icons/react';
import React, { useMemo } from 'react';
import { Button } from './Button';
import { ButtonVariant } from '../../Types/Enums';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../Store/Store';
import { setModal } from '../../Store/Actions/modalActions';

interface InfoButtonProps {
    configKey: string;
    className?: string;
    stopPropagation?: boolean;
    customButton?: {
        variant: ButtonVariant;
        content: React.ReactNode;
    };
    customContent?: React.ReactNode;
}

const InfoButton = ({
    configKey,
    className,
    stopPropagation = false,
    customButton,
    customContent,
}: InfoButtonProps) => {
    const i18n = useSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useDispatch();

    const translations = useMemo(() => {
        const title = i18n.translate({
            id: `infobutton.${configKey}.title`,
            sourceName: 'InfoButton',
        });
        const tooltip = i18n.translate({
            id: `infobutton.${configKey}.tooltip`,
            sourceName: 'InfoButton',
        });

        const content = !customContent
            ? i18n.translate({
                  id: `infobutton.${configKey}.content`,
                  sourceName: 'InfoButton',
              })
            : null;

        const hasValidTranslations =
            title &&
            tooltip &&
            !title.includes(`infobutton.${configKey}.title`) &&
            !tooltip.includes(`infobutton.${configKey}.tooltip`) &&
            (customContent || (content && !content.includes(`infobutton.${configKey}.content`)));

        return {
            title,
            content,
            tooltip,
            hasValidTranslations,
        };
    }, [i18n, configKey, customContent]);

    const handleClick = (e: React.MouseEvent) => {
        if (stopPropagation) e.stopPropagation();
        e.preventDefault();
        const modalData = {
            state: 'open',
            showLeftButton: false,
            showRightButton: false,
            title: translations.title,
            content: customContent || translations.content,
        };

        dispatch(setModal(modalData));
    };

    if (!translations.hasValidTranslations) {
        return null;
    }

    return (
        <Button
            className={`absolute top-4 right-4 ${className}`}
            variant={customButton?.variant ?? 'transparentRound'}
            onClick={handleClick}
            tooltipText={translations.tooltip}
        >
            {customButton?.content ?? <Info size={20} />}
        </Button>
    );
};

export default InfoButton;
